import { pacificStamp, type ActivityRequest, type CantTellWhy, type EvidenceRow, type FoundRow, type MissEvidence, type MissField, type Verdict } from '@claims/core';
import { FoundApprove } from './found.js';

const LIGHTNING = 'https://sweetjames.lightning.force.com/';
const TOLD: { key: keyof NonNullable<ActivityRequest['told']>; label: string }[] = [
  { key: 'policy', label: 'Policy number' }, { key: 'insured', label: "Insured's name" }, { key: 'insuredPhone', label: "Insured's phone" },
  { key: 'dob', label: "Client's date of birth" }, { key: 'vin', label: 'VIN' }, { key: 'plate', label: 'Plate' }, { key: 'dol', label: 'Date of loss' },
  { key: 'location', label: 'Location' },
  { key: 'dialed', label: 'Number dialed' },
];

/** An open miss row (Cesar 2026-10-22: "clearly show why the miss occurred, and link to CAN so we can verify why it missed"). */
export function MissDetail({ r, ev, found, approveOn = false }: { r: ActivityRequest; ev?: MissEvidence; found?: FoundRow; approveOn?: boolean }) {
  // a request that never went out, or was stopped before dialing, told no carrier anything: it only carried these
  const reached = !(r.notSent || r.dialed === false);
  return (
    <div className="tm-detail">
      <p className="tm-why">{r.missReason ?? 'No reason came back.'}</p>
      {r.told ? (
        <table className="tm-told">
          <thead><tr><th /><th>{reached ? 'What the AI told the carrier' : 'What the request carried (it never reached a carrier)'}</th></tr></thead>
          <tbody>{TOLD.map((t) => <tr key={t.key}><td className="tm-k">{t.label}</td><td className={r.told![t.key] ? 'tm-v' : 'tm-v mt'}>{r.told![t.key] ?? 'not given'}</td></tr>)}</tbody>
        </table>
      ) : <p className="mt">What the AI told the carrier was not read.</p>}
      {ev ? <EvidenceBlock ev={ev} /> : null}
      {found ? <FoundApprove row={found} approveOn={approveOn} /> : null}
      <div className="tm-links">
        <a href={`${LIGHTNING}${r.canId}`} target="_blank" rel="noreferrer">AI call {r.name}</a>
        {r.insuranceId && !r.insuranceId.startsWith('gap:') ? <a href={`${LIGHTNING}${r.insuranceId}`} target="_blank" rel="noreferrer">Insurance record</a> : null}
        {r.matterId ? <a href={`${LIGHTNING}${r.matterId}`} target="_blank" rel="noreferrer">Case</a> : null}
        {r.recordingUrl ? <a href={r.recordingUrl} target="_blank" rel="noreferrer">Recording</a>
          : r.dialed === true && r.told ? <span className="mt">No recording saved for this call</span> : null}
        <a href={`${LIGHTNING}${r.canId}`} target="_blank" rel="noreferrer">Transcript (on the AI call)</a>
      </div>
    </div>
  );
}

// ---- "Did we have it?" (PR 3): the verdict on a dialed miss, with the evidence behind it ------------------------------------------------
export const VERDICT_WORDS: Readonly<Record<Exclude<Verdict, 'carrier_side'>, string>> = Object.freeze({
  in_files_before: 'In our files before the call', fixed_after: 'Fixed after the call', not_in_readable_files: 'Not in the files we can read', cant_tell: "Can't tell",
});
export type EvidenceStatus = 'loading' | 'error' | 'ok';

/** A dot plus the words (never colour alone). Carrier side, a failed read and a row with no evidence show nothing. */
export function VerdictBadge({ ev, status }: { ev: MissEvidence | undefined; status: EvidenceStatus }) {
  if (!ev) return status === 'loading' ? <span className="vd vd-loading" role="status"><i aria-hidden="true" />Checking our files{'\u2026'}</span> : null;
  if (ev.verdict === 'carrier_side') return null;
  return <span className={`vd vd-${ev.verdict}`}><i aria-hidden="true" />{VERDICT_WORDS[ev.verdict]}</span>;
}

const FIELD_LABEL: Record<MissField, string> = { policy: 'Policy number', insured: "Insured's name", dob: "Client's date of birth", vin: 'VIN', plate: 'Plate',
  phone: "Insured's phone", address: 'Address', dol: 'Date of loss', location: 'Location' };

// a document or the intake has no link of its own (policy_documents returns no document id): its link opens the case, and says so
const CASE_LINK = new Set<NonNullable<EvidenceRow['source']>['kind']>(['card', 'dec_page', 'police_report', 'intake']);
function Source({ s }: { s: NonNullable<EvidenceRow['source']> }) {
  // each source carries its own time; the intake has none: its label ("on file now") says so
  return <span className="ev-src">{s.label}{s.at ? ` \u00b7 ${pacificStamp(s.at)}` : ''}{s.href ? <> <a href={s.href} target="_blank" rel="noopener">{CASE_LINK.has(s.kind) ? 'open case' : 'open'}</a></> : null}</span>;
}

// "Our files" when nothing came back for the row: "nothing found" only when the sources were read and held nothing (not_in_readable_files)
const EMPTY_WHY: Readonly<Record<CantTellWhy, string>> = Object.freeze({ read_failed: 'could not be read', ambiguous_party: 'two records on this side share the carrier',
  no_carrier: 'the record has no carrier', no_record: 'no insurance record to check', no_side: 'not a record type the system checks (liability and UM/UIM only)', unproven: 'no different number dated before the call', not_checked: 'not checked' });
function emptyOurs(x: EvidenceRow, ev: MissEvidence): string {
  if (!x.checked) return 'not checked';
  if (ev.why) return EMPTY_WHY[ev.why];
  return ev.verdict === 'not_in_readable_files' ? 'nothing found' : 'not checked';
}

/** What our files held, beside what the AI said, and which sources were and were not read. */
function EvidenceBlock({ ev }: { ev: MissEvidence }) {
  if (ev.verdict === 'carrier_side') return null;
  // the finder compared the sources (a verdict, or unproven); otherwise say plainly that it did not
  const compared = ev.why === null || ev.why === 'unproven';
  return (
    <div className="tm-evid">
      {ev.rows.length ? (
        <table className="tm-told">
          <thead><tr><th /><th>The AI said</th><th>Our files</th></tr></thead>
          <tbody>{ev.rows.map((x, i) => (
            <tr key={i}><td className="tm-k">{FIELD_LABEL[x.field]}</td><td className={x.told ? 'tm-v' : 'tm-v mt'}>{x.told ?? 'not given'}</td>
              <td className={x.ours ? 'tm-v' : 'tm-v mt'}>{x.ours ?? emptyOurs(x, ev)}{x.source ? <> <Source s={x.source} /></> : null}</td></tr>))}</tbody>
        </table>
      ) : null}
      {ev.candidate ? <p className="tm-why">Our files hold {ev.candidate}.</p> : null}
      {ev.systemMaySwap ? <p className="tm-why">The system may also make this swap on its own.</p> : null}
      {ev.why === 'read_failed' ? <p className="mt">Our files could not be read for this call.</p>
        : compared ? <p className="mt">Checked: {ev.checked.join(', ')}; not checked: {ev.notChecked.join(', ')}</p>
        : <p className="mt">Our files were not compared for this call.</p>}
    </div>
  );
}

/** The page's one line when the verdict read failed (rows still render without badges), and the endpoint's notes, once per page. */
export function EvidenceNotice({ failed, notes }: { failed: boolean; notes: string[] | undefined }) {
  return <>
    {failed ? <p className="mt" role="status">The check of whether our files held the right information is unavailable right now.</p> : null}
    {notes?.map((n, i) => <p key={i} className="mt">{n}</p>)}
  </>;
}
